import React from "react";

import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useTenantData } from "contexts/tenant";
import { useNotifyStatus } from "contexts/status";
import { fetchErrorDetail } from "helpers";

import { Button, Tooltip } from "@mui/material";
import {
  ChargeScheduleMessage,
  generatedApi,
  PeriodSummary,
  PublishedChargeScheduleRead,
  useGetPlanQuery,
  usePublishPlanChargeScheduleMutation,
} from "src/store/api/generatedApi";
import {
  deployPlanFormSlice,
  selectDeployFormManualHeatOverrides,
  selectDeployPlanFormChecked,
  selectDeployPlanFormCheckedCount,
  selectDeployPlanFormIsSubmitting,
} from "src/store/slices/deployPlanFormSlice";
import { AppDispatch, useAppDispatch, useAppSelector } from "src/store/store";
import { PlanId } from "components/common/boundary/PlanId";
import { Period } from "hooks/periodIndex";
import { selectPeriodPlanEdits } from "src/store/slices/planMixesTableEditSlice";
import { typeSafeObjectKeys } from "src/utils/typeSafeObjectKeys";
import equal from "fast-deep-equal";
import {
  buildPublishPlans,
  filterPeriodPlanEdits,
  getPlanPeriodKeys,
  hasMissingMixCodeOverrides,
  unpackEdits,
} from "./utils";

interface Props {
  formPlanId: PlanId;
  periods: PeriodSummary[];
  allBackupPlanIds: PlanId[];
}

const waitUntilConfirmedChargeSchedulesConfirmed =
  (dispatch: AppDispatch, tenantName: string) =>
  async (
    publishedChargeSchedules: PublishedChargeScheduleRead[],
    planIdPeriodPairs: { planId: PlanId; period: Period }[]
  ): Promise<void> => {
    const confirmedChargeSchedules = await dispatch(
      generatedApi.endpoints.listConfirmedChargeScheduleMeta.initiate({
        tenantName,
        fdmChargeScheduleIds: publishedChargeSchedules.map(
          (publishedChargeSchedule) =>
            publishedChargeSchedule.fdm_charge_schedule_id
        ),
      })
    );
    if (confirmedChargeSchedules.data) {
      const confirmedFdmChargeScheduleIds = confirmedChargeSchedules.data.map(
        (confirmedChargeSchedule) =>
          confirmedChargeSchedule.fdm_charge_schedule_id
      );
      if (
        publishedChargeSchedules.every((publishedChargeSchedule) =>
          confirmedFdmChargeScheduleIds.includes(
            publishedChargeSchedule.fdm_charge_schedule_id
          )
        )
      ) {
        return;
      } else {
        await new Promise((r) => setTimeout(r, 2000));
        return waitUntilConfirmedChargeSchedulesConfirmed(dispatch, tenantName)(
          publishedChargeSchedules,
          planIdPeriodPairs
        );
      }
    }
  };

export const SyncWithLevel2Button = ({
  formPlanId,
  periods,
  allBackupPlanIds,
}: Props) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const { manual_heat_overrides } = useTenantData();
  const notifyStatus = useNotifyStatus();

  const dispatch = useAppDispatch();

  const deployCount = useAppSelector((state) =>
    selectDeployPlanFormCheckedCount(state, formPlanId)
  );
  const isSubmitting = useAppSelector((state) =>
    selectDeployPlanFormIsSubmitting(state, formPlanId)
  );
  const checkedPlans = useAppSelector((state) =>
    selectDeployPlanFormChecked(state, formPlanId)
  );
  const manualHeatOverrides = useAppSelector((state) =>
    selectDeployFormManualHeatOverrides(state, formPlanId)
  );

  const [publishPlan] = usePublishPlanChargeScheduleMutation();

  const { currentData: plan } = useGetPlanQuery({
    tenantName,
    planId: formPlanId,
  });
  const mixMetadata = React.useMemo(() => plan?.mix_metadata ?? [], [plan]);

  const planPeriodKeys = React.useMemo(
    () => getPlanPeriodKeys(periods, allBackupPlanIds, formPlanId),
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- formPlanId is excluded because adding it would recompute keys when only the plan changes, not the periods/backups
    [periods, allBackupPlanIds]
  );

  const periodPlanEdits = useAppSelector((state) =>
    selectPeriodPlanEdits(state, planPeriodKeys)
  );
  const filteredPeriodPlanEdits = React.useMemo(
    () => filterPeriodPlanEdits(periodPlanEdits),
    [periodPlanEdits]
  );

  const { originalRows, editedRows } = React.useMemo(() => {
    return unpackEdits(filteredPeriodPlanEdits);
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- filteredPeriodPlanEdits is derived from periodPlanEdits; using periodPlanEdits avoids double-memo
  }, [periodPlanEdits]);

  const hasEdited = typeSafeObjectKeys(originalRows).some(
    (key) => !equal(editedRows[key], originalRows[key])
  );
  const hasMissingMixCodeOverride = hasMissingMixCodeOverrides({
    checkedPlans,
    formPlanId,
    manualHeatOverrides,
    mixMetadata,
    mixCodeOverrideOptions: manual_heat_overrides,
  });

  const handleWaitUntilChargeSchedulesConfirmed = React.useMemo(
    () => waitUntilConfirmedChargeSchedulesConfirmed(dispatch, tenantName),
    [dispatch, tenantName]
  );

  const handleOnClick = React.useCallback(async () => {
    dispatch(deployPlanFormSlice.actions.submittingDeployForm(formPlanId));
    const publishedChargeSchedules = await publishPlan({
      tenantName,
      publishPlans: {
        plans: buildPublishPlans({
          checkedPlans,
          formPlanId,
          manualHeatOverrides,
          mixMetadata,
        }),
      },
    })
      .unwrap()
      .catch((error: unknown) => {
        dispatch(
          deployPlanFormSlice.actions.resetDeployFormSubmission(formPlanId)
        );
        const detail = fetchErrorDetail(error);
        notifyStatus({
          type: "error",
          text: detail ?? t("couldntSyncWithLevel2"),
        });
        return null;
      });
    if (publishedChargeSchedules) {
      await handleWaitUntilChargeSchedulesConfirmed(
        publishedChargeSchedules,
        checkedPlans
      );
      const smsPlans = publishedChargeSchedules
        .map((publishedChargeSchedule) => ({
          planId: publishedChargeSchedule.plan_id,
          period: publishedChargeSchedule.period,
        }))
        .filter(
          (pair, index, allPairs) =>
            allPairs.findIndex(
              (p) => p.period === pair.period && p.planId === pair.planId
            ) === index
        );
      const messageTemplatePromises = smsPlans.map((smsPlan) => {
        const { planId, period } = smsPlan;
        return dispatch(
          generatedApi.endpoints.getChargeScheduleMessageTemplate.initiate(
            {
              tenantName,
              planId,
              period,
            },
            { forceRefetch: true }
          )
        );
      });

      const messageTemplates = await Promise.all(messageTemplatePromises);

      if (messageTemplates[0]) {
        const periodMessages = messageTemplates
          .map((messageTemplate) => messageTemplate.data)
          .filter(
            (
              messageTemplate: ChargeScheduleMessage | undefined
            ): messageTemplate is ChargeScheduleMessage =>
              messageTemplate !== undefined
          )
          .filter(({ plan_id }) => plan_id === formPlanId)
          .map(({ period, text }) => ({
            period: period as Period,
            message: text,
          }));

        const backupMessages = messageTemplates
          .map((messageTemplate) => messageTemplate.data)
          .filter(
            (
              messageTemplate: ChargeScheduleMessage | undefined
            ): messageTemplate is ChargeScheduleMessage =>
              messageTemplate !== undefined
          )
          .filter(({ plan_id }) => plan_id !== formPlanId)
          .map(({ plan_id, text }) => ({
            planId: plan_id as PlanId,
            message: text,
          }));

        dispatch(
          deployPlanFormSlice.actions.setMessageForm([
            formPlanId,
            periodMessages,
            backupMessages,
          ])
        );
      }
    }
  }, [
    publishPlan,
    dispatch,
    formPlanId,
    handleWaitUntilChargeSchedulesConfirmed,
    checkedPlans,
    manualHeatOverrides,
    mixMetadata,
    notifyStatus,
    t,
    tenantName,
  ]);

  const disabledReason = hasEdited
    ? t("pleaseSaveOrDiscardChanges")
    : hasMissingMixCodeOverride
      ? t("pleaseSelectMixCodeOverride")
      : undefined;

  return (
    <Tooltip title={disabledReason}>
      <span>
        <Button
          disabled={deployCount === 0 || hasEdited || hasMissingMixCodeOverride}
          loading={isSubmitting}
          variant="contained"
          onClick={() => void handleOnClick()}
        >
          {t("syncWithLevel2")}
        </Button>
      </span>
    </Tooltip>
  );
};
